// ./src/components/features/Forms/FormsTable/FormsTable.tsx
import React, { useMemo } from "react";
import { Text, Group } from "@mantine/core";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  AppButton,
  StatusBadge,
} from "@/components/generic";
import { Form } from "@/utils/api";

export interface FormsTableProps {
  /** Array of forms to display */
  forms: Form[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditForm?: (form: Form) => void;

  /** Callback when edit fields action is clicked */
  onEditFormFields?: (form: Form) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateForm?: (form: Form) => void;

  /** Callback when remove action is clicked */
  onRemoveForm?: (form: Form) => void;

  /** Callback when get code action is clicked */
  onGetCode?: (form: Form) => void;

  /** ID of form currently being removed */
  removingFormId?: string | null;

  /** ID of form just edited or added */
  editedItemId?: string | null | undefined;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const FormsTable: React.FC<FormsTableProps> = ({
  forms,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditForm,
  onEditFormFields,
  onDuplicateForm,
  onRemoveForm,
  onGetCode,
  removingFormId,
  editedItemId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<Form>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Name & Description",
        sortable: true,
        flex: true,
        minWidth: 250,
        render: (value, record) => (
          <div>
            <Text fw={500}>{value || "Untitled Form"}</Text>
            {record.description && (
              <Text size="xs" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
          </div>
        ),
      },
      {
        key: "path",
        label: "Form ID",
        sortable: false,
        width: 350,
        minWidth: 350,
        align: "left",
        render: (value) => {
          if (!value) return <Text c="dimmed">—</Text>;
          return (
            <Text size="sm" ff="monospace">
              {value}
            </Text>
          );
        },
      },
      {
        key: "submissionsQty",
        label: "Submissions",
        sortable: false,
        width: 150,
        minWidth: 150,
        align: "left",
        render: (value, record) => {
          if (record.submissionsQtyLimit) {
            return (
              <Text>
                {value}/{record.submissionsQtyLimit}
              </Text>
            );
          }
          return <Text>{value}</Text>;
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "actions",
        label: "",
        width: 320,
        minWidth: 320,
        align: "left",
        render: (_, record: any) => (
          <Group gap="xs" wrap="nowrap" justify="flex-end">
            <AppButton
              variant="tertiary"
              size="xs"
              iconName="Code"
              onClick={() => onGetCode?.(record)}
              disabled={removingFormId === record.id}
              tooltip="Get form code"
            />
            <AppButton
              variant="tertiary"
              size="xs"
              iconName="Forms"
              onClick={() => onEditFormFields?.(record)}
              disabled={removingFormId === record.id}
              tooltip="Edit form fields"
            />
            <RecordActions
              context={record}
              onEditClick={onEditForm}
              onRemoveClick={onRemoveForm}
              onDuplicateClick={onDuplicateForm}
              removeInProgress={removingFormId === record.id}
            />
          </Group>
        ),
      },
    ],
    [
      onEditForm,
      onEditFormFields,
      onDuplicateForm,
      onRemoveForm,
      onGetCode,
      removingFormId,
    ]
  );

  return (
    <AppTable
      data={forms}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(form) => form?.id || ""}
      highlightRow={(form) => form?.id == editedItemId}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
